import { Box, Flex, Text, type BoxProps } from '@chakra-ui/react';

interface ProgressBarProps extends BoxProps {
  /** 0-100. Values outside the range are clamped. */
  value: number;
  /** Optional left/right caption row above the track. */
  label?: string;
  valueLabel?: string;
  /** Track thickness. Defaults to '6px' (list rows); player cards use '8px'. */
  thickness?: string;
  /** Fill color token. Defaults to brand.500 — the workspace primary. NOT
   * brand.solid, which resolves to the darker hover stop. */
  fill?: string;
}

/**
 * DS progress bar: pill-radius track on bg.subtle with a brand fill. Chakra's
 * stock Progress has no DS recipe, and the courses/player surfaces need the
 * caption row anyway, so this stays a tiny local composition of primitives.
 */
export function ProgressBar({
  value,
  label,
  valueLabel,
  thickness = '6px',
  fill = 'brand.500',
  ...rest
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <Box {...rest}>
      {(label || valueLabel) && (
        <Flex justify='space-between' gap={2} fontSize='xs' color='fg.muted' mb={1.5}>
          <Text>{label}</Text>
          <Text flexShrink={0}>{valueLabel}</Text>
        </Flex>
      )}
      <Box
        role='progressbar'
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        h={thickness}
        borderRadius='full'
        bg='bg.subtle'
        overflow='hidden'
      >
        <Box h='100%' w={`${clamped}%`} bg={fill} borderRadius='full' transition='width 0.3s' />
      </Box>
    </Box>
  );
}
